import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Logo from '../components/Logo';
import {
  CalendarDays,
  Users,
  CalendarCheck,
  Bell,
  BarChart3,
  Settings,
  LogOut,
  Building2,
  Euro,
  AlertTriangle,
  Menu,
  X,
  ArrowLeft,
} from 'lucide-react';
import LocationSwitcher, { clearPracticeContext } from '../components/LocationSwitcher';
import OnboardingWizard from '../components/OnboardingWizard';

const NAV = [
  { to: '/heute', key: 'today', icon: CalendarDays },
  { to: '/warteliste', key: 'waitlist', icon: Users },
  { to: '/termine', key: 'appointments', icon: CalendarCheck },
  { to: '/slot-aktivierung', key: 'slotActivation', icon: Bell },
  { to: '/statistik', key: 'statistics', icon: BarChart3 },
  { to: '/einstellungen', key: 'settings', icon: Settings },
];
const SUPERADMIN_NAV = [
  { to: '/kunden', key: 'customers', icon: Building2 },
  { to: '/superadmin/revenue', key: 'revenue', icon: Euro },
];

export default function DashboardLayout() {
  const { t } = useTranslation();
  const { user, practice, activePracticeId, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subStatus, setSubStatus] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mainPractice, setMainPractice] = useState(null);

  // Determine if we should show the topbar "back" button:
  //   • superadmin viewing a practice → back to /einstellungen (platform overview)
  //   • Hauptstandort admin viewing one of their branches → back to own Hauptstandort
  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' || !practice) { setMainPractice(null); return; }
    if (practice.is_main_location) { setMainPractice(null); return; }
    // Branch context — fetch the main practice in this group.
    api.get('/api/locations').then((r) => {
      const all = r.data?.locations || [];
      setMainPractice(all.find((l) => l.is_main_location) || null);
    }).catch(() => {});
  }, [user, practice?._id, practice?.is_main_location]);

  function switchToMain() {
    if (!mainPractice) return;
    api.defaults.headers['X-Practice-Id'] = mainPractice._id;
    localStorage.setItem('studioops_active_practice', mainPractice._id);
    window.location.assign('/heute');
  }

  // Auto-close the mobile drawer on route change.
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open on mobile.
  useEffect(() => {
    if (sidebarOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [sidebarOpen]);

  // Subscription status banner — only relevant for tenants (admin/assistant)
  // or for a superadmin who has impersonated a practice via the Standort-
  // Switcher. We do NOT block login on this; suspended users still see the
  // dashboard but with a warning + non-functional core features.
  useEffect(() => {
    let cancelled = false;
    if (!user || user.role === 'superadmin' && !activePracticeId) {
      setSubStatus(null);
      return () => { cancelled = true; };
    }
    api.get('/api/stripe/subscription')
      .then((r) => { if (!cancelled) setSubStatus(r.data); })
      .catch(() => { if (!cancelled) setSubStatus(null); });
    return () => { cancelled = true; };
  }, [user, activePracticeId, practice?._id]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  const practiceName = practice?.name || (user?.role === 'superadmin' ? 'StudioOps · Super Admin' : '—');
  const fullName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : '';

  // Show suspended banner if subscription is past_due/canceled/incomplete.
  const isSuspended = subStatus && ['past_due', 'canceled', 'unpaid', 'incomplete_expired'].includes(subStatus.subscription_status);
  // Show "choose plan" banner for tenants without any subscription.
  const needsPlan = subStatus && (subStatus.subscription_status === 'inactive' || !subStatus.plan);

  // AVV version watcher — independent of subscription state. Hides while
  // loading so we don't flash a banner that disappears.
  const [avvStatus, setAvvStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!user || (user.role === 'superadmin' && !activePracticeId)) {
      setAvvStatus(null);
      return () => { cancelled = true; };
    }
    function refresh() {
      api.get('/api/avv/status')
        .then((r) => { if (!cancelled) setAvvStatus(r.data); })
        .catch(() => { if (!cancelled) setAvvStatus(null); });
    }
    refresh();
    window.addEventListener('studioops:avv-updated', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('studioops:avv-updated', refresh);
    };
  }, [user, activePracticeId, practice?._id, location.pathname]);
  const avvNeedsSigning = !!avvStatus?.needs_signing;

  // Pending-cancellation banner (Hauptstandort only — but the subStatus
  // endpoint already returns cancel_at_period_end for the relevant tenant).
  const cancelAtPeriodEnd = !!subStatus?.cancel_at_period_end;
  const scheduledCancelAt = subStatus?.scheduled_cancel_at;

  return (
    <div className="flex min-h-screen bg-background">
      {/* Hamburger — fixed top-left, visible only below lg breakpoint (1024px). */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-50 flex items-center justify-center rounded-md shadow-md transition-colors active:scale-95"
        style={{ backgroundColor: '#1B3A6B', color: '#fff', width: 44, height: 44 }}
        aria-label="Menü öffnen"
        data-testid="hamburger-btn"
      >
        <Menu size={22} strokeWidth={2.2}/>
      </button>

      {/* Dark overlay behind the sliding sidebar — only when open on small screens. */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50 backdrop-blur-[2px] transition-opacity"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
          aria-hidden="true"
        />
      )}

      {/* Sidebar — always visible at >= lg, slide-in drawer at < lg. */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-[280px] lg:w-60 flex flex-col bg-navy text-white transform transition-transform duration-300 ease-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        data-testid="sidebar"
        data-state={sidebarOpen ? 'open' : 'closed'}
        style={{ backgroundColor: '#1B3A6B' }}
      >
        <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
          <Logo variant="light" />
          {/* Close button — only on small screens, inside the drawer. */}
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/70 hover:text-white p-1 -mr-1"
            aria-label="Menü schließen"
            data-testid="sidebar-close-btn"
          >
            <X size={20}/>
          </button>
        </div>

        {/* Mobile-only: full-width Standort switcher inside the drawer. */}
        <div className="lg:hidden px-3 pt-3 pb-1" data-testid="mobile-location-switcher-wrap">
          <LocationSwitcher variant="mobile" />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `so-sidebar-link ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.key}`}
              >
                <Icon size={18} strokeWidth={2} />
                <span>{t(`nav.${item.key}`)}</span>
              </NavLink>
            );
          })}
          {user?.role === 'superadmin' && (
            <>
              <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-white/40">Super-Admin</div>
              {SUPERADMIN_NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `so-sidebar-link ${isActive ? 'active' : ''}`}
                    data-testid={`nav-${item.key}`}
                  >
                    <Icon size={18} strokeWidth={2} />
                    <span>{t(`nav.${item.key}`)}</span>
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>
        <div className="px-4 py-4 border-t border-white/10 text-xs text-white/60">
          <p className="font-mono">v1.0 · Production Ready</p>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 lg:ml-60 min-w-0">
        {/* Top bar */}
        <header
          className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-white/95 backdrop-blur px-4 sm:px-6 py-3 pl-16 lg:pl-6 gap-3"
          data-testid="topbar"
        >
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide text-text-secondary font-semibold truncate">
              {t('app.name')}
            </p>
            <h2 className="text-sm sm:text-base font-semibold text-text truncate" data-testid="practice-name">
              {practiceName}
            </h2>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Context-aware back button */}
            {user?.role === 'superadmin' && activePracticeId && (
              <button
                onClick={clearPracticeContext}
                className="so-btn so-btn-secondary text-xs"
                data-testid="back-to-platform-btn"
                title={t('topbar.backToPlatform')}
              >
                <ArrowLeft size={14}/>
                <span className="hidden md:inline">{t('topbar.backToPlatform')}</span>
              </button>
            )}
            {user?.role === 'admin' && mainPractice && (
              <button
                onClick={switchToMain}
                className="so-btn so-btn-secondary text-xs"
                data-testid="back-to-main-btn"
                title={t('topbar.backToMain', { name: mainPractice.location_label || mainPractice.name })}
              >
                <ArrowLeft size={14}/>
                <span className="hidden md:inline truncate max-w-[160px]">{t('topbar.backToMain', { name: mainPractice.location_label || mainPractice.name })}</span>
              </button>
            )}
            <div className="hidden lg:block">
              <LocationSwitcher variant="topbar"/>
            </div>
            <div className="lg:hidden">
              <LocationSwitcher variant="mobile-label"/>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-[11px] text-text-secondary">{t('topbar.loggedInAs')}</p>
              <p className="text-sm font-medium text-text truncate max-w-[180px]" data-testid="user-name">
                {fullName} <span className="text-text-secondary font-normal">· {user?.role}</span>
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="so-btn so-btn-secondary"
              data-testid="logout-btn"
              aria-label={t('common.logout')}
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">{t('common.logout')}</span>
            </button>
          </div>
        </header>

        <main className="px-4 sm:px-6 py-6 sm:py-8 max-w-[1400px]" data-testid="main-content">
          {cancelAtPeriodEnd && scheduledCancelAt && (
            <div
              className="mb-6 rounded-md border bg-white p-4 flex items-start gap-3"
              data-testid="cancel-pending-banner"
              style={{ borderLeftWidth: 4, borderLeftColor: '#D97706', backgroundColor: '#FFFBEB' }}
            >
              <AlertTriangle size={18} style={{ color: '#D97706' }} className="shrink-0 mt-0.5"/>
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: '#92400E' }}>{t('banners.cancelPendingTitle')}</p>
                <p className="text-xs mt-0.5" style={{ color: '#92400E' }}>
                  {t('banners.cancelPendingBody', { date: new Date(scheduledCancelAt).toLocaleDateString(t('locale.numberFormat') || 'de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) })}
                </p>
              </div>
              <Link to="/einstellungen/abo" className="so-btn so-btn-secondary text-xs shrink-0" data-testid="cancel-pending-banner-cta">{t('banners.manageBilling')}</Link>
            </div>
          )}
          {avvNeedsSigning && (
            <div
              className="mb-6 rounded-md border bg-white p-4 flex items-start gap-3"
              data-testid="avv-version-banner"
              style={{ borderLeftWidth: 4, borderLeftColor: '#D97706', backgroundColor: '#FFFBEB' }}
            >
              <AlertTriangle size={18} style={{ color: '#D97706' }} className="shrink-0 mt-0.5"/>
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: '#92400E' }}>
                  {avvStatus?.outdated ? t('banners.avvOutdated') : t('banners.avvUnsigned')}
                </p>
                <p className="text-xs mt-0.5" style={{ color: '#92400E' }}>
                  {t('banners.requiredVersion')}: <strong>{avvStatus?.version || '1.0'}</strong>
                  {avvStatus?.avv_version && avvStatus.outdated ? <> · {t('banners.yourVersion')}: <strong>{avvStatus.avv_version}</strong></> : null}
                </p>
              </div>
              <Link
                to="/einstellungen"
                className="so-btn text-xs shrink-0"
                style={{ backgroundColor: '#D97706', color: '#fff' }}
                data-testid="avv-version-banner-cta"
              >{t('banners.signNow')}</Link>
            </div>
          )}
          {isSuspended && (
            <div
              className="mb-6 rounded-md border border-danger/30 bg-danger/5 p-4 flex items-start gap-3"
              data-testid="suspended-banner"
              style={{ borderLeftWidth: 4, borderLeftColor: '#DC2626' }}
            >
              <AlertTriangle size={18} className="text-danger shrink-0 mt-0.5"/>
              <div className="flex-1">
                <p className="text-sm font-semibold text-danger">{t('banners.suspendedTitle')}</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {t('banners.status')}: <strong>{subStatus.subscription_status}</strong>. {t('banners.suspendedBody')}
                </p>
              </div>
              <Link to="/einstellungen" className="so-btn so-btn-primary text-xs shrink-0" data-testid="suspended-banner-cta">{t('banners.goToSubscription')}</Link>
            </div>
          )}
          {needsPlan && !isSuspended && (
            <div
              className="mb-6 rounded-md border border-accent/30 bg-accent/5 p-4 flex items-start gap-3"
              data-testid="needs-plan-banner"
            >
              <AlertTriangle size={18} className="text-accent shrink-0 mt-0.5"/>
              <div className="flex-1">
                <p className="text-sm font-semibold text-accent">{t('banners.needsPlanTitle')}</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {t('banners.needsPlanBody')}
                </p>
              </div>
              <Link to="/einstellungen" className="so-btn so-btn-primary text-xs shrink-0" data-testid="needs-plan-cta">{t('banners.choosePlan')}</Link>
            </div>
          )}
          <Outlet />
        </main>
      </div>
      {/* Onboarding wizard — admin-only, first-login, shown until completed.
          Critical: only render when the active practice context matches the
          admin's OWN practice. A Hauptstandort admin who has switched into a
          branch location (via X-Practice-Id) MUST NOT see the branch's wizard
          — they cannot PATCH /api/practices/:branchId (canAccessPractice
          returns 403 for any practice other than their own). The branch's own
          admin sees their own wizard when they log in directly. */}
      {user?.role === 'admin'
        && practice
        && practice._id === user.practice_id
        && !practice.wizard_completed
        && <OnboardingWizard />}
    </div>
  );
}
